import { useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import jonPhoto from "@/assets/profile-photo.webp.asset.json";
import { cn } from "@/lib/utils";

export type NavLink = {
  to: string;
  label: string;
  active?: boolean;
};

export type NavCTA = {
  href: string;
  label: string;
  external?: boolean;
};

// The site navigation is intentionally identical on every page.
// Per-page `links` / `cta` props are accepted for backwards compatibility but ignored.
const NAV_LINKS: NavLink[] = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/work", label: "Work" },
  { to: "/library", label: "Library" },
  { to: "/guides", label: "Guides" },
  { to: "/hiring", label: "Hiring" },
  { to: "/contact", label: "Contact" },
];

const NAV_CTA: NavCTA = {
  href: "https://cal.com/vibinc/30min",
  label: "Talk to Jon",
  external: true,
};

export function SiteNav(_props?: { links?: NavLink[]; cta?: NavCTA }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const links = NAV_LINKS.map((l) => ({
    ...l,
    active: l.to === "/" ? pathname === "/" : pathname.startsWith(l.to),
  }));
  const cta = NAV_CTA;

  const [collapsed, setCollapsed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const lastYRef = useRef(0);
  const mobileContentRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const openRef = useRef(false);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    lastYRef.current = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setCollapsed(y > 24);
      // Don't hide/show while the mobile menu is open — user is interacting.
      if (openRef.current) {
        lastYRef.current = y;
        return;
      }
      const last = lastYRef.current;
      const delta = y - last;
      if (delta > 12 && y > 120) {
        setHidden(true);
      } else if (delta < -12) {
        setHidden(false);
      }
      lastYRef.current = y;
    };
    setCollapsed(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (mobileContentRef.current) {
      setContentHeight(mobileContentRef.current.scrollHeight);
    }
  }, [links.length, open]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-[80] w-full bg-transparent pointer-events-none md:transition-[padding] md:duration-500 md:ease-[cubic-bezier(0.22,1,0.36,1)]",
      )}
      style={{
        paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)",
      }}
    >
      <div
        className={cn(
          "relative z-10 mx-auto px-4 pointer-events-auto transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
          collapsed ? "max-w-[1100px] md:px-6" : "max-w-[1400px] md:px-10",
        )}
      >
        <nav
          aria-label="Primary"
          className={cn(
            "transition-[border-radius,padding,background-color,backdrop-filter,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
            "rounded-2xl backdrop-blur-xl bg-background/75 ring-1 ring-border/70 shadow-[0_10px_40px_-20px_rgba(20,20,40,0.35)] md:overflow-hidden [isolation:isolate]",
            "md:flex md:items-center md:justify-between",
            collapsed
              ? "md:pl-5 md:pr-3 md:h-16"
              : "md:ring-0 md:shadow-none md:bg-transparent md:backdrop-blur-0 md:rounded-none md:h-24",
          )}
        >
          <div
            className={cn(
              "flex items-center justify-between md:contents",
              "pl-3 pr-1.5 h-14 md:px-0 md:h-auto",
            )}
          >
            <Link to="/" className="flex items-center" aria-label="thevibingco home">
              <img
                src={logo.url}
                alt="the Vibing Company logo"
                width={176}
                height={44}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className={cn(
                  "w-auto transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                  collapsed ? "h-7 md:h-9" : "h-7 md:h-11",
                )}
              />
            </Link>
            <div className="flex items-center gap-1 md:gap-3">
              <NavLinks links={links} collapsed={collapsed} reduceMotion={reduceMotion} />
              <div className="ml-1 md:ml-2">
                <MagneticButton
                  href={cta.href}
                  variant="dark"
                  className="px-4 py-2"
                  avatar={jonPhoto.url}
                  {...(cta.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                  <span className="relative inline-grid">
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.span
                        key={cta.label}
                        className="col-start-1 row-start-1 whitespace-nowrap"
                        initial={
                          reduceMotion
                            ? { opacity: 0 }
                            : { opacity: 0, filter: "blur(6px)", y: -5 }
                        }
                        animate={
                          reduceMotion
                            ? { opacity: 1 }
                            : { opacity: 1, filter: "blur(0px)", y: 0 }
                        }
                        exit={
                          reduceMotion
                            ? { opacity: 0 }
                            : { opacity: 0, filter: "blur(6px)", y: 5 }
                        }
                        transition={{ type: "spring", duration: 0.4, bounce: 0 }}
                      >
                        {cta.label} <span aria-hidden>→</span>
                      </motion.span>
                    </AnimatePresence>
                  </span>
                </MagneticButton>
              </div>
            </div>
          </div>

          {/* Mobile menu section — part of the same pill */}
          <div
            className="md:hidden bg-foreground/[0.04] overflow-hidden rounded-b-2xl"
            style={{
              maxHeight: hidden ? 0 : 600,
              borderTopWidth: hidden ? 0 : 1,
              transition:
                "max-height 500ms cubic-bezier(0.22,1,0.36,1), border-top-width 300ms ease-out",
            }}
            aria-hidden={hidden}
            inert={hidden ? true : undefined}
          >
            <button
              type="button"
              ref={toggleRef}
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={open}
              aria-controls="mobile-nav-links"
              style={{ touchAction: "manipulation" }}
              className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-foreground select-none"
            >
              <span>Menu</span>
              <span
                aria-hidden
                className="inline-flex h-6 w-6 items-center justify-center transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
                style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M3 5l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            <div
              id="mobile-nav-links"
              style={{
                maxHeight: open ? 600 : 0,
                opacity: open ? 1 : 0,
                transition:
                  "max-height 500ms cubic-bezier(0.22,1,0.36,1), opacity 350ms ease-out",
              }}
              className="overflow-hidden"
              inert={open ? undefined : true}
            >
              <div
                ref={mobileContentRef}
                className="px-3 pb-3 pt-0 flex flex-col"
                style={{
                  transform: open ? "translateY(0)" : "translateY(-6px)",
                  transition:
                    "transform 400ms cubic-bezier(0.22,1,0.36,1)",
                }}
              >
                {links.map((l) => (
                  <Link
                    key={l.to + l.label}
                    to={l.to}
                    aria-current={l.active ? "page" : undefined}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "px-3 py-2.5 rounded-xl text-base transition-colors",
                      l.active
                        ? "text-foreground bg-foreground/5"
                        : "text-muted-foreground hover:text-foreground hover:bg-foreground/5",
                    )}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
}

const MotionLink = motion.create(Link);

function NavLinks({
  links,
  collapsed,
  reduceMotion = false,
}: {
  links: NavLink[];
  collapsed: boolean;
  reduceMotion?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [pill, setPill] = useState<{ left: number; width: number; visible: boolean }>({
    left: 0,
    width: 0,
    visible: false,
  });

  const moveTo = (idx: number) => {
    const container = containerRef.current;
    const el = itemRefs.current[idx];
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    setPill({ left: eRect.left - cRect.left, width: eRect.width, visible: true });
  };

  return (
    <motion.div
      ref={containerRef}
      layout={!reduceMotion}
      transition={{ type: "spring", duration: 0.45, bounce: 0 }}
      className="relative hidden md:flex items-center gap-0.5"
      onMouseLeave={() => setPill((p) => ({ ...p, visible: false }))}
      style={{
        transform: collapsed && !reduceMotion ? "translateX(-6px)" : "translateX(0)",
        opacity: collapsed ? 0.9 : 1,
        transitionProperty: "transform, opacity",
        transitionDuration: "600ms",
        transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      <div
        aria-hidden
        className="absolute top-1/2 -translate-y-1/2 h-8 rounded-full bg-foreground/5 pointer-events-none"
        style={{
          left: pill.left,
          width: pill.width,
          opacity: pill.visible ? 1 : 0,
          transition:
            "left 350ms cubic-bezier(0.22,1,0.36,1), width 350ms cubic-bezier(0.22,1,0.36,1), opacity 200ms ease-out",
        }}
      />
      <AnimatePresence mode="popLayout" initial={false}>
        {links.map((l, i) => (
          <MotionLink
            key={l.to + l.label}
            to={l.to}
            layout={!reduceMotion}
            ref={(el: HTMLAnchorElement | null) => {
              itemRefs.current[i] = el;
            }}
            aria-current={l.active ? "page" : undefined}
            onMouseEnter={() => moveTo(i)}
            initial={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, filter: "blur(6px)", y: -6, scale: 0.96 }
            }
            animate={
              reduceMotion
                ? { opacity: 1 }
                : { opacity: 1, filter: "blur(0px)", y: 0, scale: 1 }
            }
            exit={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, filter: "blur(6px)", y: 6, scale: 0.98 }
            }
            transition={{
              type: "spring",
              duration: 0.4,
              bounce: 0,
              delay: reduceMotion ? 0 : i * 0.045,
            }}
            className={cn(
              "relative z-10 inline-flex items-center text-sm px-3 py-1.5 rounded-full will-change-transform transition-colors hover:text-foreground",
              l.active ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {l.label}
          </MotionLink>
        ))}
      </AnimatePresence>
    </motion.div>
  );
}

